import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/authContext";
import { fetchCurrentAccount } from "@/api/account";
import { setRuntimeAuthToken } from "@/api/client";
import "./helloLandingBeta.css";
import { BETA_API } from "@/api/betaAccess";

/** Invitation links carry the secret in the fragment (never sent to servers or logs).
 * Read it once, then remove it from the address bar and browser history entry. */
function readInvitationFragment() {
  if (typeof window === "undefined" || !window.location.hash) return { code: "", email: "" };
  const fragment = new URLSearchParams(window.location.hash.slice(1));
  const code = (fragment.get("code") || "").slice(0, 128);
  const email = (fragment.get("email") || "").slice(0, 254);
  if (code || email) window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search);
  return { code, email };
}

export default function BetaJoinPage() {
  const [invitation] = useState(readInvitationFragment);
  const linked = invitation.code.length >= 20;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { completeLogin } = useAuth();
  return <main className="hb-root"><div className="hb-shell hb-section"><div className="hb-cta">
    <p className="hb-kicker">ArtistNav private beta</p>
    <h1 className="hb-h2">Join with your invitation</h1>
    <p className="hb-section-lead">{linked ? <>Invitation applied ✓ — create your account for <strong>{invitation.email}</strong>.</> : "An invitation code is required to create an account."}</p>
    <form className="hb-form hb-join" onSubmit={async (event) => {
      event.preventDefault(); if (busy) return; setBusy(true); setError("");
      const fields = new FormData(event.currentTarget);
      const params = new URLSearchParams(window.location.search);
      const attribution = Object.fromEntries(["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"]
        .filter(key => params.has(key)).map(key => [key, params.get(key)!.slice(0, 160)]));
      try {
        const response = await fetch(`${BETA_API}/register`, {method:"POST", headers:{"Content-Type":"application/json"},
          body: JSON.stringify({name:fields.get("name"),email:fields.get("email"),password:fields.get("password"),invitation_code:fields.get("invitation_code"),attribution}), signal:AbortSignal.timeout(30000)});
        if (!response.ok) throw new Error(response.status === 403 ? "Invitation is invalid, expired or fully redeemed." : response.status === 409 ? "An account already exists for this email. Sign in instead, or reset your password." : "Registration could not be completed. Please try again.");
        const data = await response.json();
        if (!data.access_token) throw new Error("Registration could not be confirmed.");
        setRuntimeAuthToken(data.access_token);
        // Persist the session before the best-effort profile fetch so a transient
        // profile failure never leaves a created account with a consumed invite.
        completeLogin(data.access_token, {user:{email:fields.get("email"),is_owner:false}});
        try { completeLogin(data.access_token, await fetchCurrentAccount()); } catch { /* onboarding refreshes */ }
        // Hand the name and email to onboarding so the user isn't asked again; the
        // password was just set here, so the account step must not require it.
        const fullName = String(fields.get("name") ?? "").trim().split(/\s+/);
        navigate("/signup/account", {replace:true, state:{passwordSet:true, prefillAccount:{
          email:String(fields.get("email") ?? "").trim(), first_name:fullName[0] ?? "", last_name:fullName.slice(1).join(" ")}}});
      } catch (reason) {setError(reason instanceof Error ? reason.message : "Unable to register.");} finally {setBusy(false);}
    }}>
      <label>Your name<input name="name" autoComplete="name" required maxLength={120} disabled={busy} /></label>
      <label>Email address<input name="email" type="email" autoComplete="email" required maxLength={254} disabled={busy}
        defaultValue={invitation.email} readOnly={linked && Boolean(invitation.email)} /></label>
      {linked
        ? <input name="invitation_code" type="hidden" value={invitation.code} />
        : <label>Invitation code<input name="invitation_code" type="password" autoComplete="off" required minLength={20} maxLength={128} disabled={busy} /></label>}
      <label>Password (at least 12 characters)<input name="password" type="password" autoComplete="new-password" required minLength={12} maxLength={128} disabled={busy} /></label>
      {error && <p role="alert">{error}</p>}
      <button className="hb-btn" disabled={busy}>{busy ? "Creating account…" : "Create beta account"}</button>
    </form>
    <p className="hb-form-alt">Already have an account? <a href="/login">Sign in</a></p>
  </div></div></main>;
}
